// DEPOSIT ADDRESSES (UI rebuild batch 4 · custody setup · reference build for the wallet lane) — Astra's "find or issue a
// customer's deposit address": one searchable register (customer / label, network, address, check), Issue address as the
// page's one gold button (its own surface, IssueAddress.tsx), and each address opened in a detail sheet where the full
// address is read first, then — only after this browser re-derives it — its QR (components/v2/custody-admin/verify.tsx).
//
// Behaviour kept from screens/custody/Addresses.tsx: the same reads and keys (['custody','customers'],
// ['custody','deposit-addresses', customer], 30 s refresh), the customer filter (now behind Filters), Tag — once — with an
// optional deal and its refusals word for word, Enter sends the tag, the dated notes minus the "issued" line. A failed
// refresh keeps the rows with one notice (listState); "none" only after a successful read.
import { SigninQuiet } from '../../../components/v2/custody/SigninLine';
import { isCustodySignin } from '../../../lib/errors';
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Plus } from 'lucide-react';
import { custody as api, type DepositAddress } from '../../../lib/api';
import { ApiError } from '../../../lib/errors';
import { fmtDate } from '../../../lib/format';
import { listState } from '../../../lib/custody-screen';
import { shortAddress } from '../../../lib/custody-format';
import {
  PageHead, Panel, Notice, Register, SearchBar, SkeletonRows, EmptyState, StateLine, DetailSheet, Fact, TAP, whenColumn, type RegisterColumn,
} from '../../../components/v2/kit';
import { CheckPill, GiveOut, checkOf } from '../../../components/v2/custody-admin/verify';
import { CA } from '../../../components/v2/custody-admin/paths';

const textOf = (err: unknown): string => String((err as { message?: unknown } | null)?.message ?? err);

/** The tag's refusals, in the operator's words (screens/custody/Addresses.tsx tagRefusal, verbatim). */
function tagRefusal(err: unknown): { line: string; more: string } {
  const reason = err instanceof ApiError && typeof err.body.reason === 'string' ? err.body.reason : null;
  switch (reason) {
    case 'already_tagged': return { line: 'Already tagged — a tag is given once.', more: textOf(err) };
    case 'party': return { line: 'No such customer in the Books.', more: textOf(err) };
    case 'deal': return { line: 'No such deal in the Books.', more: textOf(err) };
    case 'deal_not_theirs': return { line: 'That deal is another customer\'s.', more: textOf(err) };
    case 'deal_voided': return { line: 'That deal was voided.', more: textOf(err) };
    // The server's own guard: the address is not what its registered key puts at its path (27 Oct 2026).
    case 'address_mismatch': return { line: 'Address does not match its key. Not tagged.', more: textOf(err) };
    default: return { line: "Couldn't tag — try again.", more: textOf(err) };
  }
}

/** His own dated lines, minus the "issued" line the desk writes. */
const notesOf = (a: DepositAddress): string[] =>
  (a.notes ?? '').split('\n').map((l) => l.trim()).filter((l) => l && !/^\d{4}-\d{2}-\d{2}: issued /.test(l));

const nameOf = (a: DepositAddress): string => a.partyName ?? a.partySlug ?? 'Untagged';
/** The row's quiet second line: its deal alone — notes and the issue month live in the opened address (Astra, 2 Nov 2026). */
const subOf = (a: DepositAddress): string => (a.dealId ? `Deal ${a.dealId}` : '');

type Customer = { slug: string; name: string };

/** `embedded`: drawn inside custody's Deposits & addresses screen (its Addresses filter) — the screen's own head is that one's. */
export function DepositAddressesV2({ embedded = false }: { embedded?: boolean } = {}) {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState('');
  const customer = params.get('customer') ?? '';
  const openId = params.get('address');
  const customers = useQuery({ queryKey: ['custody', 'customers'], queryFn: api.customers });
  const list = useQuery({
    queryKey: ['custody', 'deposit-addresses', customer],
    queryFn: () => api.depositAddresses(customer || undefined),
    refetchInterval: 30_000,
  });
  const state = listState(list);
  const all = list.data?.addresses ?? [];
  const needle = q.trim().toLowerCase();
  const rows = useMemo(() => (needle
    ? all.filter((a) => [a.partyName, a.partySlug, a.dealId, a.notes, a.label, a.address].some((v) => (v ?? '').toLowerCase().includes(needle)))
    : all), [all, needle]);
  const open = openId ? all.find((a) => String(a.id) === openId) ?? null : null;
  const setParam = (k: string, v: string | null) => { const n = new URLSearchParams(params); if (v) n.set(k, v); else n.delete(k); setParams(n); };

  const columns: RegisterColumn<DepositAddress>[] = [
    whenColumn<DepositAddress>((a) => a.issuedAt),
    { key: 'who', head: 'Customer / label', cell: (a) => (
      <span className="block min-w-0">
        <span className={`line-clamp-2 break-words font-medium ${a.partySlug ? '' : 'text-tg-hint'}`} title={nameOf(a)}>{nameOf(a)}</span>
        {subOf(a) && <span className="mt-1 block truncate text-meta text-tg-hint">{subOf(a)}</span>}
      </span>
    ) },
    { key: 'net', head: 'Network', width: 'w-[8rem]', cell: () => <span className="font-mono">TRC20</span> },
    { key: 'addr', head: 'Address', align: 'right', width: 'w-[12rem]', cell: (a) => <span className="font-mono">{shortAddress(a.address, 8, 4)}</span> },
    { key: 'state', head: 'State', align: 'right', width: 'w-[10rem]', cell: (a) => <span className="inline-flex items-center gap-2"><CheckPill check={checkOf(a)} /><ChevronRight className="h-4 w-4 text-tg-hint" aria-hidden /></span> },
  ];
  const retry = <button type="button" className={TAP} disabled={list.isFetching} onClick={() => void list.refetch()}>{list.isFetching ? 'Trying…' : 'Try again'}</button>;

  return (
    <>
      {!embedded && (
        <PageHead
          title="Addresses"
          inline
          actions={(
            <Link to={CA.issue} className="btn-primary inline-flex min-h-11 items-center gap-2 px-4 text-body lg:min-h-10">
              <Plus className="h-4 w-4" aria-hidden />Issue address
            </Link>
          )}
        />
      )}
      <SearchBar
        value={q}
        onChange={setQ}
        placeholder="Search name, deal or note"
        active={customer ? 1 : 0}
        filters={(
          <label className="block max-w-sm">
            <span className="mb-1 block text-meta text-tg-hint">Customer</span>
            <select className="select" value={customer} onChange={(e) => setParam('customer', e.target.value || null)}>
              <option value="">Every address</option>
              {(customers.data?.customers ?? []).map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
            {customers.isError && !isCustodySignin(customers.error) && <StateLine tone="bad" className="mt-1" more={textOf(customers.error)}>Couldn&rsquo;t read the customers.</StateLine>}
          </label>
        )}
      />

      {(state.show === 'failed' || state.stale) && isCustodySignin(list.error) ? <SigninQuiet error={list.error} /> : state.show === 'failed' || state.stale ? (
        <Notice tone="bad">
          <StateLine tone="bad" more={textOf(list.error)} action={retry}>
            {state.stale ? <>Couldn&rsquo;t refresh — showing the last read.</> : <>Couldn&rsquo;t read the addresses.</>}
          </StateLine>
        </Notice>
      ) : null}

      {state.show === 'pending' ? (
        <Panel flush><SkeletonRows rows={4} what="addresses" /></Panel>
      ) : rows.length > 0 ? (
        <Panel flush>
          <Register
            label="Deposit addresses"
            columns={columns}
            rows={rows}
            rowKey={(a) => String(a.id)}
            onOpen={(a) => setParam('address', String(a.id))}
            card={(a) => ({
              title: <>{nameOf(a)}{subOf(a) && <span className="mt-1 block truncate text-meta font-normal text-tg-hint">{subOf(a)}</span>}</>,
              figure: <ChevronRight className="h-4 w-4 text-tg-hint" aria-hidden />,
              meta: <span className="font-mono">TRC20 · {shortAddress(a.address, 8, 4)}</span>,
              state: <CheckPill check={checkOf(a)} />,
            })}
          />
          <div className="flex justify-between gap-3 border-t border-stroke-soft px-4 py-3 text-meta text-tg-hint lg:px-5">
            <span>{state.show === 'ready' && !state.stale ? `${rows.length} ${rows.length === 1 ? 'address' : 'addresses'} · newest first` : ''}</span>
            <span>Dubai time</span>
          </div>
        </Panel>
      ) : state.show === 'ready' && !state.stale ? (
        <EmptyState>{needle || customer ? 'Nothing here.' : 'No deposit addresses yet.'}</EmptyState>
      ) : null}

      {open && <AddressSheet a={open} customers={customers.data?.customers ?? null} customersFailed={customers.isError && !isCustodySignin(customers.error)} onClose={() => setParam('address', null)} />}
    </>
  );
}

/** One address: read it, then (checked) its QR and Copy; its facts; Tag once when no customer is on it. */
function AddressSheet({ a, customers, customersFailed, onClose }: { a: DepositAddress; customers: Customer[] | null; customersFailed: boolean; onClose: () => void }) {
  const check = checkOf(a);
  const notes = notesOf(a);
  return (
    <DetailSheet title={nameOf(a)} onClose={onClose}>
      <GiveOut address={a.address} check={check} label={nameOf(a)} />
      <div className="mt-5 border-t border-stroke-soft">
        <Fact label="Network">USDT·TRC</Fact>
        <Fact label="Path">{a.derivation ? <span className="font-mono">{a.derivation}</span> : 'not issued from a key'}</Fact>
        <Fact label="Deal">{a.dealId ?? '—'}</Fact>
        {a.issuedAt && <Fact label="Issued">{fmtDate(a.issuedAt)}</Fact>}
        {notes.length > 0 && <Fact label="Notes">{notes.map((n) => <span key={n} className="block">{n}</span>)}</Fact>}
      </div>
      {/* No Tag on a Mismatch (the wallet lane, 27 Oct 2026): an address the key does not derive is given to nobody. */}
      {!a.partySlug && check !== 'mismatch' && <TagForm a={a} customers={customers} customersFailed={customersFailed} />}
    </DetailSheet>
  );
}

/** Tag — once — from the customer list; a deal is optional. A form: Enter sends it like the button (23 Oct 2026). */
function TagForm({ a, customers, customersFailed }: { a: DepositAddress; customers: Customer[] | null; customersFailed: boolean }) {
  const qc = useQueryClient();
  const [slug, setSlug] = useState('');
  const [dealId, setDealId] = useState('');
  const tag = useMutation({
    mutationFn: () => api.tagDepositAddress(a.id, { partySlug: slug, ...(dealId.trim() ? { dealId: dealId.trim() } : {}) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['custody', 'deposit-addresses'] });
      void qc.invalidateQueries({ queryKey: ['custody', 'deposits'] });
    },
  });
  const refusal = tag.isError ? tagRefusal(tag.error) : null;
  return (
    <form className="mt-5 space-y-3 border-t border-stroke-soft pt-4" onSubmit={(e) => { e.preventDefault(); if (slug && !tag.isPending) tag.mutate(); }}>
      <h3 className="text-body font-semibold">Tag customer</h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_8rem]">
        <label className="block min-w-0">
          <span className="mb-1 block text-meta text-tg-hint">Customer</span>
          <select className="select" value={slug} onChange={(e) => setSlug(e.target.value)} disabled={tag.isPending || !customers}>
            <option value="">Choose…</option>
            {(customers ?? []).map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-meta text-tg-hint">Deal (optional)</span>
          <input className="input" value={dealId} placeholder="D-12" onChange={(e) => setDealId(e.target.value)} disabled={tag.isPending} />
        </label>
      </div>
      {customersFailed && <StateLine tone="bad">Couldn&rsquo;t read the customers.</StateLine>}
      {refusal && <StateLine tone="bad" more={refusal.more}>{refusal.line}</StateLine>}
      {tag.isSuccess && <StateLine tone="good">Tagged.</StateLine>}
      <button type="submit" className="btn-secondary min-h-11 px-4 text-body lg:min-h-10" disabled={!slug || tag.isPending}>{tag.isPending ? 'Tagging…' : 'Tag'}</button>
    </form>
  );
}
